// React Query hooks for the user's billing state.
//
// Two hooks, both auth-gated:
//   • useUserTier(userId?)        → the user's tier as a single string
//   • useScoringEligibility()     → { allowed, used, limit, remaining }
//
// Both short-circuit to a sensible default when there's no signed-in
// user so the component tree never has to gate on auth state.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOnAppResume } from "@/hooks/useOnAppResume";
import {
  checkScoringEligibility,
  fetchUserTier,
} from "@/lib/billing/subscriptions";
import type { ScoringEligibility, SubscriptionTier } from "@/lib/billing/types";

// Tier rarely changes — only on subscription events (Pro signup,
// downgrade, cancel) which are infrequent. The PlanBadge renders many
// times per page (one per author / list row / suggestion card), each
// firing its own useUserTier query. Bumping the stale window from
// 1 min → 10 min cuts the cross-screen "tier RPC chatter" by an
// order of magnitude without risking stale display: a brand-new Pro
// signup would see their own badge update via React Query's mutation
// onSettled invalidation path (when that lands in v1.1).
const TIER_STALE_MS = 10 * 60_000;       // 10 min
const ELIGIBILITY_STALE_MS = 10_000;     // 10 s — counter bumps after upload

export const userTierQueryKey = (userId: string) =>
  ["userTier", userId] as const;

export const scoringEligibilityQueryKey = (userId: string) =>
  ["scoringEligibility", userId] as const;

/**
 * Tier for any user (defaults to the caller). Used by PlanBadge to
 * label profile cards / feed authors / list rows. The RPC is
 * SECURITY DEFINER so it works for any user_id even though the
 * underlying user_subscriptions table is RLS-locked.
 */
export function useUserTier(targetUserId?: string) {
  const { user } = useAuth();
  const callerId = user?.id ?? null;
  const lookupId = targetUserId ?? callerId;

  const query = useQuery<SubscriptionTier, Error>({
    queryKey: userTierQueryKey(lookupId ?? "anon"),
    enabled: !!lookupId,
    staleTime: TIER_STALE_MS,
    queryFn: async () => fetchUserTier(targetUserId),
  });

  return {
    tier: query.data ?? null,
    isLoading: query.isPending,
    refetch: query.refetch,
  };
}

/**
 * Re-read the signed-in user's OWN tier whenever the app returns to the
 * foreground.
 *
 * In-app Play purchases already invalidate the tier (usePlayerUpgrade).
 * Everything else that changes it happens server-side while the app may be
 * running — a web/Razorpay payment landing via webhook, a renewal, an
 * expiry at current_period_end — and nothing else would refetch it: the
 * query stales at 10 min and the app disables refetch on focus/mount. So
 * without this, the badge (and ad-free state, which reads the same query)
 * stayed on the old tier until a cold start.
 *
 * refetchType "all" also refreshes the cached entry when no screen showing
 * it is mounted, because refetchOnMount:false would otherwise keep serving
 * the stale value when the profile opens. Only the caller's own key: one
 * get_user_tier RPC per resume, not one per badge on screen.
 * Mount once, globally (AppShell).
 */
export function useRefreshOwnTierOnResume() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  useOnAppResume(() => {
    if (!userId) return;
    void queryClient.invalidateQueries({
      queryKey: userTierQueryKey(userId),
      refetchType: "all",
    });
  });
}

/**
 * Eligibility for the CURRENT user only — the RPC implicitly uses
 * auth.uid(). Drives:
 *   • The "X/Y AI scores used this month" indicator on MyVideos
 *   • The pre-flight check in PlayerHome's upload flow
 *   • The "Monthly limit reached" modal
 */
export function useScoringEligibility() {
  const { user } = useAuth();
  const callerId = user?.id ?? null;

  return useQuery<ScoringEligibility, Error>({
    queryKey: scoringEligibilityQueryKey(callerId ?? "anon"),
    enabled: !!callerId,
    staleTime: ELIGIBILITY_STALE_MS,
    // Opening a screen that shows the counter must re-read it. The global
    // default is refetchOnMount:false, which would serve a cached count
    // after a card was spent elsewhere — wrong for a usage display, and
    // cheap to avoid given the 10s staleTime.
    refetchOnMount: true,
    queryFn: checkScoringEligibility,
  });
}

/**
 * Re-read the caller's quota whenever the app returns to the foreground.
 *
 * Same reasoning as useRefreshOwnTierOnResume: cards are spent server-side
 * (analyze-video calls consume_report_quota after the card commits) and
 * granted server-side (a referred friend's first scored video pays the
 * referrer), both while this app may be backgrounded. Nothing else would
 * refetch it — the query stales at 10s but refetch-on-focus is off
 * globally, so a resumed app would keep showing the count from before.
 *
 * refetchType "all" so the cached entry updates even when no screen
 * showing it is mounted. Mount it wherever the counter is displayed.
 */
export function useRefreshScoringEligibilityOnResume() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  useOnAppResume(() => {
    if (!userId) return;
    void queryClient.invalidateQueries({
      queryKey: scoringEligibilityQueryKey(userId),
      refetchType: "all",
    });
  });
}
